import React, { useState } from 'react';
import { Table, Tag, Alert, Modal, Form, Input, message, Button, Space, Descriptions, Image } from 'antd';
import { EditOutlined, EyeOutlined, CheckCircleOutlined, CloseCircleOutlined, SyncOutlined } from '@ant-design/icons';
import axios from 'axios';
import moment from 'moment';
import AuthService from '../../utils/auth';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3060/api';

const DOCUMENT_TYPE_LABEL = {
  passport: '护照',
  nz_driver_license: 'NZ驾照',
  nz_senior_card: 'NZ老年卡',
};

const StepTouristCheck = ({ tourists, completeness, tour, onRefresh }) => {
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingTourist, setEditingTourist] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [detailTourist, setDetailTourist] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const isDaytour = tour?.tourType === 'daytour';
  const allTouristDetails = completeness?.tourists || [];
  // 一日游排除已取消和已退款的游客
  const touristDetails = isDaytour
    ? allTouristDetails.filter(t => t.orderStatus !== 'cancelled' && t.orderStatus !== 'refunded')
    : allTouristDetails;
  const stats = completeness?.tour?.touristStats;

  const handleViewDetail = (tourist) => {
    const full = tourists.find(t => t.id === tourist.id);
    if (!full) return;
    setDetailTourist(full);
    setDetailModalVisible(true);
  };

  const handleEdit = (tourist) => {
    const full = tourists.find(t => t.id === tourist.id);
    if (!full) return;
    setEditingTourist(full);
    form.setFieldsValue({
      touristName: full.touristName,
      passportName: full.passportName,
      passportNumber: full.passportNumber,
      documentNumber: full.documentNumber,
      contactPhone: full.contactPhone,
      contactEmail: full.contactEmail,
    });
    setEditModalVisible(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      await axios.put(
        `${API_BASE}/tourists/${editingTourist.id}`,
        values,
        { headers: AuthService.getAuthHeaders() }
      );
      message.success('更新成功');
      setEditModalVisible(false);
      onRefresh();
    } catch (err) {
      if (err.errorFields) return;
      message.error('更新失败: ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  const getUploadStatusTag = (status) => {
    const map = {
      verified: { color: 'success', icon: <CheckCircleOutlined />, text: '已验证' },
      uploaded: { color: 'processing', icon: <SyncOutlined spin />, text: '待审核' },
      pending: { color: 'default', icon: <CloseCircleOutlined />, text: '未上传' },
      rejected: { color: 'error', icon: <CloseCircleOutlined />, text: '已拒绝' },
    };
    const config = map[status] || map.pending;
    return <Tag color={config.color} icon={config.icon}>{config.text}</Tag>;
  };

  const getOrderStatusTag = (status) => {
    const map = {
      confirmed: { color: 'success', text: '已确认' },
      pending: { color: 'warning', text: '待确认' },
    };
    const config = map[status] || { color: 'default', text: status || '-' };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const columns = [
    ...(!isDaytour ? [
      {
        title: '护照姓名',
        dataIndex: 'passportName',
        key: 'passportName',
        width: 150,
        fixed: 'left',
        render: (val) => val || <Tag color="warning">未填写</Tag>,
      },
      {
        title: '规格',
        key: 'spec',
        width: 120,
        render: (_, r) => {
          const P = { with_tour: ['Plus款', 'blue'], no_tour: ['基础款', 'default'] }[r.packageType];
          const T = { '2star': ['二钻', 'green'], '3star': ['三钻', 'gold'], '4star': ['四钻', 'volcano'], '5star': ['五钻', 'red'] }[r.hotelTier];
          const tags = [];
          if (P) tags.push(<Tag key="p" color={P[1]} style={{ margin: 0 }}>{P[0]}</Tag>);
          if (T) tags.push(<Tag key="t" color={T[1]} style={{ margin: 0 }}>{T[0]}</Tag>);
          if (!tags.length) return <span style={{ color: '#bbb', fontSize: 12 }}>默认规格</span>;
          return <Space size={4} wrap>{tags}</Space>;
        },
      },
      {
        title: '护照号',
        dataIndex: 'passportNumber',
        key: 'passportNumber',
        width: 130,
        render: (val) => val || '-',
      },
      {
        title: '护照状态',
        dataIndex: 'uploadStatus',
        key: 'uploadStatus',
        width: 100,
        render: (status) => getUploadStatusTag(status),
      },
    ] : [
      {
        title: '姓名',
        dataIndex: 'touristName',
        key: 'touristName',
        width: 120,
        fixed: 'left',
      },
      {
        title: '联系电话',
        dataIndex: 'phone',
        key: 'phone',
        width: 130,
        render: (val) => val || '-',
      },
      {
        title: '上车地点',
        dataIndex: 'pickupPoint',
        key: 'pickupPoint',
        width: 140,
        render: (val) => val || <Tag color="warning">未选择</Tag>,
      },
      {
        title: '状态',
        dataIndex: 'orderStatus',
        key: 'orderStatus',
        width: 90,
        render: (status) => getOrderStatusTag(status),
      },
    ]),
    {
      title: '销售',
      dataIndex: 'salespersonName',
      key: 'salespersonName',
      width: 120,
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_, record) => (
        <Space size={4}>
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record)}
          >
            详情
          </Button>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
        </Space>
      ),
    },
  ];

  // 统计：后端已按一日游排除取消的游客计算
  const confirmedCount = isDaytour
    ? touristDetails.filter(t => t.orderStatus === 'confirmed').length
    : 0;
  const docReadyCount = isDaytour
    ? confirmedCount
    : (stats?.passportReady ?? 0);
  const docReadyLabel = isDaytour ? '已确认游客' : '护照已验证';
  const docReadyTotal = isDaytour ? touristDetails.length : (stats?.total || 0);

  const missingItems = [];
  if (isDaytour) {
    const pendingCount = touristDetails.filter(t => t.orderStatus === 'pending').length;
    if (pendingCount > 0) {
      missingItems.push(`${pendingCount} 位游客订单待确认`);
    }
  } else {
    if (stats && docReadyCount < (stats.total || 0)) {
      const remaining = (stats.total || 0) - docReadyCount;
      missingItems.push(`${remaining} 位游客护照未验证`);
    }
  }
  if (stats && stats.hasSalespersonEmail < stats.total) {
    missingItems.push(`${stats.total - stats.hasSalespersonEmail} 位游客的销售缺少邮箱`);
  }

  return (
    <div>
      <div className="stat-cards">
        <div className={`stat-card ${docReadyCount === docReadyTotal ? 'success' : 'warning'}`}>
          <div className="stat-value">{docReadyCount}/{docReadyTotal}</div>
          <div className="stat-label">{docReadyLabel}</div>
        </div>
        <div className={`stat-card ${stats?.hasSalespersonEmail === stats?.total ? 'success' : 'warning'}`}>
          <div className="stat-value">{stats?.hasSalespersonEmail || 0}/{stats?.total || 0}</div>
          <div className="stat-label">有销售邮箱</div>
        </div>
      </div>

      {missingItems.length > 0 && (
        <Alert
          type="warning"
          showIcon
          message="以下项目需要完善"
          description={
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {missingItems.map((item, i) => <li key={i}>{item}</li>)}
            </ul>
          }
          style={{ marginBottom: 16 }}
        />
      )}

      <Table
        columns={columns}
        dataSource={touristDetails}
        rowKey="id"
        size="small"
        scroll={{ x: 800 }}
        pagination={false}
      />

      <Modal
        title={isDaytour ? '编辑游客信息（一日游）' : '编辑游客信息'}
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        onOk={handleSave}
        confirmLoading={saving}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="touristName" label="游客姓名" rules={[{ required: true, message: '请输入姓名' }]}>
            <Input />
          </Form.Item>
          {isDaytour ? (
            <Form.Item name="documentNumber" label="证件号码">
              <Input placeholder="身份证号或其他证件号" />
            </Form.Item>
          ) : (
            <>
              <Form.Item name="passportName" label="护照姓名">
                <Input />
              </Form.Item>
              <Form.Item name="passportNumber" label="护照号码">
                <Input />
              </Form.Item>
            </>
          )}
          <Form.Item name="contactPhone" label="联系电话">
            <Input />
          </Form.Item>
          <Form.Item name="contactEmail" label="联系邮箱">
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`游客详情 - ${detailTourist?.passportName || detailTourist?.touristName || ''}`}
        open={detailModalVisible}
        onCancel={() => { setDetailModalVisible(false); setDetailTourist(null); }}
        footer={null}
        width={650}
      >
        {detailTourist && (
          <div>
            {(detailTourist.passportPhoto || detailTourist.documentPhoto) && (
              <div style={{ textAlign: 'center', marginBottom: 16 }}>
                <Image
                  src={(() => {
                    const photo = isDaytour ? (detailTourist.documentPhoto || detailTourist.passportPhoto) : (detailTourist.passportPhoto || detailTourist.documentPhoto);
                    return photo.startsWith('http') ? photo : `${API_BASE.replace('/api', '')}${photo}`;
                  })()}
                  alt={isDaytour ? '证件照片' : '护照照片'}
                  style={{ maxWidth: '100%', maxHeight: 400 }}
                />
              </div>
            )}
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="录入姓名">{detailTourist.touristName || '-'}</Descriptions.Item>
              {!isDaytour && detailTourist.passportName && detailTourist.passportName.trim().toUpperCase() !== (detailTourist.touristName || '').trim().toUpperCase() && (
                <Descriptions.Item label="护照姓名"><span style={{ color: '#ff4d4f' }}>{detailTourist.passportName}</span></Descriptions.Item>
              )}
              {isDaytour ? (
                <>
                  <Descriptions.Item label="证件类型">{DOCUMENT_TYPE_LABEL[detailTourist.documentType] || detailTourist.documentType || '-'}</Descriptions.Item>
                  <Descriptions.Item label="证件号码">{detailTourist.documentNumber || detailTourist.passportNumber || '-'}</Descriptions.Item>
                </>
              ) : (
                <>
                  <Descriptions.Item label="护照号码">{detailTourist.passportNumber || '-'}</Descriptions.Item>
                  <Descriptions.Item label="国籍">{detailTourist.nationality || '-'}</Descriptions.Item>
                </>
              )}
              <Descriptions.Item label="性别">{detailTourist.gender === 'M' ? '男' : detailTourist.gender === 'F' ? '女' : '-'}</Descriptions.Item>
              <Descriptions.Item label="出生日期">
                {(detailTourist.passportBirthDate || detailTourist.birthDate)
                  ? moment(detailTourist.passportBirthDate || detailTourist.birthDate).format('YYYY-MM-DD') : '-'}
              </Descriptions.Item>
              {!isDaytour && (
                <>
                  <Descriptions.Item label="护照签发日">{detailTourist.passportIssueDate ? moment(detailTourist.passportIssueDate).format('YYYY-MM-DD') : '-'}</Descriptions.Item>
                  <Descriptions.Item label="护照有效期">{detailTourist.passportExpiryDate ? moment(detailTourist.passportExpiryDate).format('YYYY-MM-DD') : '-'}</Descriptions.Item>
                </>
              )}
              <Descriptions.Item label={isDaytour ? '状态' : '护照状态'}>
                {isDaytour ? getOrderStatusTag(detailTourist.orderStatus) : getUploadStatusTag(detailTourist.uploadStatus)}
              </Descriptions.Item>
              {detailTourist.uploadStatus === 'rejected' && detailTourist.rejectionReason && (
                <Descriptions.Item label="拒绝原因" span={2}><span style={{ color: '#ff4d4f' }}>{detailTourist.rejectionReason}</span></Descriptions.Item>
              )}
              <Descriptions.Item label="联系电话">{detailTourist.contactPhone || detailTourist.phone || '-'}</Descriptions.Item>
              <Descriptions.Item label="联系邮箱">{detailTourist.contactEmail || '-'}</Descriptions.Item>
              {isDaytour && (
                <Descriptions.Item label="上车点">{detailTourist.pickupPoint || '-'}</Descriptions.Item>
              )}
              <Descriptions.Item label="销售">
                {detailTourist.salesperson?.name || detailTourist.salespersonName || '-'}
                {detailTourist.salesperson?.email ? `（${detailTourist.salesperson.email}）` : ''}
              </Descriptions.Item>
              <Descriptions.Item label="EKOK">{detailTourist.ekok || '-'}</Descriptions.Item>
              <Descriptions.Item label="订单号">{detailTourist.orderNumber || '-'}</Descriptions.Item>
              <Descriptions.Item label="添加时间">{detailTourist.createdAt ? moment(detailTourist.createdAt).format('YYYY-MM-DD HH:mm') : '-'}</Descriptions.Item>
            </Descriptions>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default StepTouristCheck;
